import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Divider,
    useTheme,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Breadcrumbs,
    Link,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, FieldArray, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../../helpers/AuthProvider";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import MainDrawer from "../../MainDrawer";

// --- Data for Dropdowns ---
const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg' },
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg' }
];
const gcpRegionList = [ "africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2", "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2", "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1", "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2", "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9", "me-central1", "me-central2", "me-west1", "northamerica-northeast1", "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1", "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2", "us-west3", "us-west4"];
const gcpSolutionCategoryTypeList = [ { value: 'composer', label: 'Cloud Composer' }, { value: 'dataproc', label: 'Dataproc' }, { value: 'dataflow', label: 'Dataflow' }];
const awsRegionList = [ "us-east-1", "us-east-2", "us-west-1", "us-west-2", "af-south-1", "ap-east-1", "ap-south-1", "ap-northeast-1", "ap-northeast-2", "ap-northeast-3", "ap-southeast-1", "ap-southeast-2", "ca-central-1", "eu-central-1", "eu-west-1", "eu-west-2", "eu-south-1", "eu-west-3", "eu-north-1", "me-south-1", "sa-east-1"];
const awsSolutionCategoryTypeList = [ { value: 'mwaa', label: 'Managed Workflows for Apache Airflow (MWAA)' }, { value: 'emr', label: 'Elastic MapReduce (EMR)' }, { value: 'glue', label: 'AWS Glue' }];
const azureRegionList = ["eastus", "eastus2", "westus", "westus2", "westus3", "centralus", "northcentralus", "southcentralus", "canadacentral", "canadaeast", "uksouth", "ukwest", "northeurope", "westeurope", "francecentral", "germanywestcentral", "japaneast", "japanwest", "koreacentral", "koreasouth", "australiaeast", "australiasoutheast", "southafricanorth", "eastasia", "southeastasia", "brazilsouth", "uaenorth"];
const azureSolutionCategoryTypeList = [ { value: 'data-factory', label: 'Azure Data Factory' }, { value: 'hdinsight', label: 'HDInsight' }, { value: 'synapse', label: 'Azure Synapse Analytics' }];
const environmentSizeList = ["small", "medium", "large"];

// --- Interfaces ---
interface CostLabelPair { id?: string; cost: string; label: string; }
interface DataPipelineFormValues { id?: string; name: string; applicationName: string; solutionCategoryType: string; cloudProvider: string; projectId: string; networkProjectId: string; region: string; environment: string; environmentSize: string; costCenterValue: string; costLabelPairs: CostLabelPair[]; }
interface ConfigData { gcpProjectId: string; }
interface FetchedPipelineData { id: string; name: string; environment: string; }
interface ApplicationData { id: string; applicationName: string; environment: string; }
interface AddDataPipelinePageProps { isEdit: boolean; }
interface DataPipelineFormContentProps { isEdit: boolean; }
interface DynamicFieldConfig { labels: { projectId: string; networkProjectId: string; }; dropdowns: { regions: string[]; solutionTypes: { value: string; label: string }[]; }; defaults: { region: string; solutionType: string; } }

// --- Component to handle dynamic field updates ---
interface DynamicFieldsUpdaterProps {
    isEdit: boolean;
    initialFormValuesForEdit: DataPipelineFormValues | null;
    configData: ConfigData | null;
    setDynamicFields: React.Dispatch<React.SetStateAction<DynamicFieldConfig>>;
}

const DynamicFieldsUpdater: React.FC<DynamicFieldsUpdaterProps> = ({ isEdit, initialFormValuesForEdit, configData, setDynamicFields }) => {
    const { values, setFieldValue } = useFormikContext<DataPipelineFormValues>();

    useEffect(() => {
        let config: DynamicFieldConfig;
        switch (values.cloudProvider) {
            case 'aws':
                config = { labels: { projectId: 'Account ID', networkProjectId: 'VPC ID' }, dropdowns: { regions: awsRegionList, solutionTypes: awsSolutionCategoryTypeList }, defaults: { region: 'us-east-1', solutionType: 'mwaa' } };
                break;
            case 'azure':
                config = { labels: { projectId: 'Subscription ID', networkProjectId: 'Virtual Network (VNet) Name' }, dropdowns: { regions: azureRegionList, solutionTypes: azureSolutionCategoryTypeList }, defaults: { region: 'eastus', solutionType: 'data-factory' }};
                break;
            case 'gcp':
            default:
                config = { labels: { projectId: 'Project ID', networkProjectId: 'Network Project ID' }, dropdowns: { regions: gcpRegionList, solutionTypes: gcpSolutionCategoryTypeList }, defaults: { region: 'asia-south1', solutionType: 'composer' }};
                break;
        }
        setDynamicFields(config);

        if (!isEdit || (initialFormValuesForEdit && values.cloudProvider !== initialFormValuesForEdit.cloudProvider)) {
            setFieldValue('region', config.defaults.region);
            setFieldValue('solutionCategoryType', config.defaults.solutionType);
            setFieldValue('environmentSize', 'small');
            setFieldValue('projectId', values.cloudProvider === 'gcp' ? (configData?.gcpProjectId || '') : '');
            setFieldValue('networkProjectId', '');
        }
    }, [values.cloudProvider, setFieldValue, isEdit, initialFormValuesForEdit, configData, setDynamicFields]);

    return null;
};

// --- Component to handle dynamic environment field ---
interface EnvironmentFieldUpdaterProps {
    applications: ApplicationData[];
    fetchedPipelines: FetchedPipelineData[];
    setEnvironmentOptions: React.Dispatch<React.SetStateAction<string[]>>;
}
const EnvironmentFieldUpdater: React.FC<EnvironmentFieldUpdaterProps> = ({ applications, fetchedPipelines, setEnvironmentOptions }) => {
    const { values, setFieldValue } = useFormikContext<DataPipelineFormValues>();

    useEffect(() => {
        if (!values.applicationName) {
            setEnvironmentOptions([]);
            return;
        }

        const availableEnvs = applications
            .filter(app => app.applicationName === values.applicationName)
            .map(app => app.environment);

        const existingEnvsForName = fetchedPipelines
            .filter(p => p.name.toLowerCase() === values.name.toLowerCase())
            .map(p => p.environment);
        
        const finalOptions = availableEnvs.filter(env => !existingEnvsForName.includes(env));
        setEnvironmentOptions(finalOptions);

        if (values.environment && !finalOptions.includes(values.environment)) {
            setFieldValue('environment', '');
        }

    }, [values.applicationName, values.name, applications, fetchedPipelines, setEnvironmentOptions, setFieldValue, values.environment]);

    return null;
}

// --- Main Form Content Component ---
const DataPipelineFormContent: React.FC<DataPipelineFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [configData, setConfigData] = useState<ConfigData | null>(null);
    const [fetchedPipelines, setFetchedPipelines] = useState<FetchedPipelineData[]>([]);
    const [applications, setApplications] = useState<ApplicationData[]>([]);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [initialFormValuesForEdit, setInitialFormValuesForEdit] = useState<DataPipelineFormValues | null>(null);
    const [isLoadingPipelineData, setIsLoadingPipelineData] = useState(false);
    const [environmentOptions, setEnvironmentOptions] = useState<string[]>([]);
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    const [dynamicFields, setDynamicFields] = useState<DynamicFieldConfig>({
        labels: { projectId: 'Project ID', networkProjectId: 'Network Project ID' },
        dropdowns: { regions: gcpRegionList, solutionTypes: gcpSolutionCategoryTypeList },
        defaults: { region: 'asia-south1', solutionType: 'composer' }
    });

    const servicesRequiringSize = ['composer', 'mwaa', 'emr', 'hdinsight'];

    // **FIX HERE**: Use Array.from(new Set(...)) for better compiler compatibility
    const uniqueApplicationNames = React.useMemo(() => {
        return Array.from(new Set(applications.map(app => app.applicationName)));
    }, [applications]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [configRes, pipelinesRes, appsRes] = await Promise.all([
                    queryGraphql(`query GetConfig { getConfig { gcpProjectId } }`),
                    queryGraphql(`query GetDataPipelines { getDataPipelines { id name environment } }`),
                    queryGraphql(`query GetApplications { applications { id applicationName environment } }`)
                ]);

                if (configRes.getConfig) setConfigData(configRes.getConfig);
                if (pipelinesRes.getDataPipelines) setFetchedPipelines(pipelinesRes.getDataPipelines.map((p: any) => ({ id: p.id, name: p.name.toLowerCase(), environment: p.environment })));
                if (appsRes.applications) setApplications(appsRes.applications);
                
            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        const fetchPipelineForEdit = async () => {
            if (isEdit && id) {
                setIsLoadingPipelineData(true);
                try {
                    const response = await queryGraphql(`
                        query GetDataPipeline($getDataPipelineId: String!) {
                          getDataPipeline(id: $getDataPipelineId) {
                            id name solutionCategoryType cloudProvider environment application { applicationName } costLabelPairs { cost label }
                            details { dataPipeline { projectId networkProjectId region environmentSize } }
                          }
                        }
                    `, { getDataPipelineId: id });

                    const pipeline = response.getDataPipeline;
                    if (pipeline) {
                        const allPairs = pipeline.costLabelPairs || [];
                        const costCenterPair = allPairs.find((p: any) => p.cost === "cost-center");
                        const dynamicPairs = allPairs.filter((p: any) => p.cost !== "cost-center").map((pair: any, index: number) => ({ ...pair, id: `clp-edit-${index}` }));
                        
                        setInitialFormValuesForEdit({
                            id: pipeline.id,
                            name: pipeline.name,
                            applicationName: pipeline.application?.applicationName || "",
                            costCenterValue: costCenterPair ? costCenterPair.label : "",
                            costLabelPairs: dynamicPairs,
                            solutionCategoryType: pipeline.solutionCategoryType,
                            cloudProvider: pipeline.cloudProvider || 'gcp',
                            projectId: pipeline.details.dataPipeline.projectId,
                            networkProjectId: pipeline.details.dataPipeline.networkProjectId,
                            region: pipeline.details.dataPipeline.region,
                            environment: pipeline.environment || "",
                            environmentSize: pipeline.details.dataPipeline.environmentSize || "",
                        });
                    } else {
                        navigate("/datasolutions/data-pipelines");
                    }
                } catch (err) {
                    console.error("Error fetching pipeline for editing:", err);
                    navigate("/datasolutions/data-pipelines");
                } finally {
                    setIsLoadingPipelineData(false);
                }
            }
        };
        fetchPipelineForEdit();
    }, [isEdit, id, navigate]);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("This field is required").max(50, "Max 50 characters"),
        applicationName: Yup.string().required("This field is required"),
        environment: Yup.string().required("This field is required"), 
        solutionCategoryType: Yup.string().required("This field is required"),
        cloudProvider: Yup.string().required("This field is required"),
        projectId: Yup.string().required("This field is required"),
        networkProjectId: Yup.string().required("This field is required"),
        region: Yup.string().required("This field is required"),
        environmentSize: Yup.string().when('solutionCategoryType', { is: (val: string) => servicesRequiringSize.includes(val), then: (schema) => schema.required('Environment size is required for this Category Type'), otherwise: (schema) => schema.optional() }),
        costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
        costLabelPairs: Yup.array().of(Yup.object().shape({
            cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
            label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
        })),
    });

    const defaultInitialValues: DataPipelineFormValues = {
        name: "",
        applicationName: "",
        solutionCategoryType: "composer",
        cloudProvider: "gcp",
        projectId: configData?.gcpProjectId || "",
        networkProjectId: "",
        region: "asia-south1",
        environment: "", 
        environmentSize: "small",
        costCenterValue: "",
        costLabelPairs: [],
    };
    
    if (isEdit && isLoadingPipelineData) {
        return <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading pipeline data...</Typography></Box>;
    }
    if (isEdit && !initialFormValuesForEdit) {
         return <Typography color="error" sx={{p:3}}>Could not load pipeline data for editing.</Typography>;
    }

    return (
        <Container sx={{ py: 3, px: { xs: 2, sm: 3, md: 4 }, flexGrow: 1 }}>
            <Formik
                initialValues={isEdit ? (initialFormValuesForEdit || defaultInitialValues) : defaultInitialValues}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting, setStatus }) => {
                    setIsSubmittingForm(true);
                    setSubmitting(true);

                    const selectedApplication = applications.find(app => app.applicationName.toLowerCase() === values.applicationName.toLowerCase() && app.environment === values.environment);
                    const applicationId = selectedApplication ? selectedApplication.id : null;
                    
                    const finalCostLabelPairs = [{ cost: "cost-center", label: values.costCenterValue }, ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))];

                    const detailsPayload: any = { projectId: values.projectId, networkProjectId: values.networkProjectId, region: values.region };

                    if (servicesRequiringSize.includes(values.solutionCategoryType)) {
                        detailsPayload.environmentSize = values.environmentSize;
                    }

                    const commonInput = {
                        name: values.name,
                        solutionCategoryType: values.solutionCategoryType,
                        cloudProvider: values.cloudProvider,
                        environment: values.environment, 
                        costLabelPairs: finalCostLabelPairs,
                        details: { dataPipeline: detailsPayload },
                        ...(applicationId && { application: applicationId })
                    };

                    const mutationString = isEdit ? `mutation UpdateSolutionDataPipeline($input: UpdateSolutionInput) { updateSolutionDataPipeline(input: $input) { id } }` : `mutation CreateSolutionDataPipeline($input: CreateSolutionInput) { createSolutionDataPipeline(input: $input) { id } }`;
                    
                    const variables = { input: isEdit ? { id, ...commonInput } : commonInput };
                    const resultKey = isEdit ? 'updateSolutionDataPipeline' : 'createSolutionDataPipeline';

                    try {
                        const data = await queryGraphql(mutationString, variables);
                        if (data[resultKey] && data[resultKey].id) {
                            navigate("/datasolutions/data-pipelines");
                        } else {
                            setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} Data Pipeline. ${JSON.stringify(data.errors)}` });
                        }
                    } catch (error: any) {
                        setStatus({ error: error.message || "An unexpected error occurred." });
                    } finally {
                        setIsSubmittingForm(false);
                        setSubmitting(false);
                    }
                }}
            >
                {({ handleChange, handleBlur, values, setFieldValue, errors, touched, isValid, dirty, status, isSubmitting }) => (
                    <Form>
                        <DynamicFieldsUpdater isEdit={isEdit} initialFormValuesForEdit={initialFormValuesForEdit} configData={configData} setDynamicFields={setDynamicFields} />
                        <EnvironmentFieldUpdater applications={applications} fetchedPipelines={fetchedPipelines} setEnvironmentOptions={setEnvironmentOptions} />

                        <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={2.5}>
                            {isEdit ? "Edit the Data Pipeline details below" : "Fill the below details to add a Data Pipeline"}
                        </Typography>
                        
                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Cloud Provider Details</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                    <ToggleButtonGroup
                                            value={values.cloudProvider}
                                            exclusive
                                            onChange={(event, newProvider) => {
                                                if (newProvider !== null) {
                                                    setFieldValue('cloudProvider', newProvider);
                                                }
                                            }}
                                            aria-label="cloud provider"
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            {providerDisplayData.map((p) => (
                                                <ToggleButton
                                                    key={p.value}
                                                    value={p.value}
                                                    aria-label={p.label}
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                        textTransform: 'none',
                                                        fontFamily: 'Gilroy-Medium',
                                                        borderRadius: '24px !important',
                                                        border: '1px solid #ccc !important',
                                                        px: 2,
                                                        py: 1,
                                                        '&.Mui-selected': {
                                                            borderColor: '#000 !important',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                        }
                                                    }}
                                                >
                                                    <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                    {p.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Pipeline Details</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Pipeline Name" /><TextField variant="filled" fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Name" size="medium" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div></Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Application Name" />
                                    <TextField select variant="filled" fullWidth name="applicationName" value={values.applicationName} onChange={handleChange} onBlur={handleBlur} size="medium" hiddenLabel error={touched.applicationName && Boolean(errors.applicationName)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                        {uniqueApplicationNames.map((appName) => (<MenuItem key={appName} value={appName} sx={{ fontFamily: "Gilroy-Medium" }}>{appName}</MenuItem>))}
                                    </TextField>
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="applicationName" /></div>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Environment" />
                                    <TextField select name="environment" value={values.environment} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" disabled={!values.applicationName || !values.name} error={touched.environment && Boolean(errors.environment)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }}
                                        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}
                                    >
                                        {/* <MenuItem value="" disabled><em>Select Environment</em></MenuItem> */}
                                        {environmentOptions.map((env) => (<MenuItem key={env} value={env} sx={{ fontFamily: "Gilroy-Medium" }}>{env}</MenuItem>))}
                                    </TextField>
                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="environment" /></div>
                                </Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={dynamicFields.labels.projectId} /><TextField variant="filled" fullWidth name="projectId" value={values.projectId} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${dynamicFields.labels.projectId}`} size="medium" hiddenLabel error={touched.projectId && Boolean(errors.projectId)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="projectId" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={dynamicFields.labels.networkProjectId} /><TextField variant="filled" fullWidth name="networkProjectId" value={values.networkProjectId} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${dynamicFields.labels.networkProjectId}`} size="medium" hiddenLabel error={touched.networkProjectId && Boolean(errors.networkProjectId)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="networkProjectId" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Region" /><TextField select name="region" value={values.region} onChange={(e) => setFieldValue("region", e.target.value)} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.region && Boolean(errors.region)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>{dynamicFields.dropdowns.regions.map((r) => (<MenuItem key={r} value={r} sx={{ fontFamily: "Gilroy-Medium" }}>{r}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="region" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Category Type" /><TextField select name="solutionCategoryType" value={values.solutionCategoryType} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.solutionCategoryType && Boolean(errors.solutionCategoryType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }}>{dynamicFields.dropdowns.solutionTypes.map((item) => (<MenuItem key={item.value} value={item.value} sx={{ fontFamily: "Gilroy-Medium" }}>{item.label}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="solutionCategoryType" /></div></Grid>
                                {servicesRequiringSize.includes(values.solutionCategoryType) && (
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Environment Size" /><TextField select name="environmentSize" value={values.environmentSize} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.environmentSize && Boolean(errors.environmentSize)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }}>{environmentSizeList.map((g) => (<MenuItem key={g} value={g} sx={{ fontFamily: "Gilroy-Medium" }}>{g}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="environmentSize" /></div></Grid>
                                )}
                            </Grid>
                        </Box>

                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3} sx={{ mb: "16px" }}>
                                <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography><TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0", fontFamily: "Gilroy-Medium" } }} /></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Value (Cost Center)" /><TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div></Grid>
                            </Grid>
                            <FieldArray
                                name="costLabelPairs"
                                render={arrayHelpers => (
                                    <div>
                                        {values.costLabelPairs.map((pair, index) => (
                                            <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }}>
                                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Key" /><TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div></Grid>
                                                <Grid item xs={12} sm={5}><CompulsoryFieldsLabel fieldName="Value" /><TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div></Grid>
                                                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 1, sm: '32px' } }}><IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton></Grid>
                                            </Grid>
                                        ))}
                                        <Button variant="text" sx={{ color: "#000000", textTransform: "none", fontFamily: "Gilroy-Medium" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>+ Add Key Value Pair</Button>
                                    </div>
                                )}
                            />
                            <FormControlLabel control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black', },mt:"1" }} />} label="I confirm that the details provided are correct and I wish to proceed." sx={{ mb:"24px", display: 'flex', justifyContent: 'flex-start', fontFamily: "Gilroy-Medium", '& .MuiTypography-root': {fontFamily: "Gilroy-Medium"} }} />
                        </Box>
                        
                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&:hover': { borderColor: 'black', backgroundColor: 'rgba(0,0,0,0.04)' } }} variant="outlined" size="large" onClick={() => navigate("/datasolutions/data-pipelines")} disabled={isSubmittingForm || isSubmitting}>Cancel</Button>
                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", "&:hover": { backgroundColor: "#333333" }, "&.Mui-disabled": { backgroundColor: "#cccccc", color: "#888888" } }} variant="contained" size="large" type="submit" disabled={isSubmittingForm || isSubmitting || !(isValid && dirty && confirmationChecked)  || isViewer }>{(isSubmittingForm || isSubmitting) ? <CircularProgress size={24} color="inherit" /> : (isEdit ? "Save Changes" : "Add New Data Pipeline")}</Button>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

// --- Page Component (Wrapper) ---
const AddDataPipelinePage: React.FC<AddDataPipelinePageProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };
    
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"datasolutions-data-pipelines"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 3, ml: {xs:2, sm:3, md:5} , mt:2 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "#555555", fontFamily: "Gilroy-Medium" }}>
                        <Link underline="hover" color="inherit" href="/datapipelines" sx={{ fontFamily: "Gilroy-Medium" }} onClick={(e)=> {e.preventDefault(); navigate("/datasolutions/data-pipelines")}}>Data Pipelines</Link>
                        <Typography color="text.primary" sx={{ fontFamily: "Gilroy-Medium" }}>{isEdit ? "Edit Data Pipeline" : "Add New Data Pipeline"}</Typography>
                    </Breadcrumbs>
                    <Box display="flex" alignItems="center" mt={1.5}>
                        <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/data-pipelines")} sx={{ mr: 0.5 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
                        <Typography fontFamily="Gilroy-Bold" fontSize="20px">{isEdit ? "Edit Data Pipeline" : "Add New Data Pipeline"}</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1, alignItems: 'flex-start', pt:0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: { xs: '95%', md: '80%' }, py: 0 }}>
                        <DataPipelineFormContent isEdit={isEdit} />
                    </Box>
                </Box>
                <MiniBot />
            </Box>
        </Box>
    );
};

export default AddDataPipelinePage;